import {getYear} from 'date-fns';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {AdvertisementPreviewPlatform} from '@dms/api/sales';
import type {GetVehicleApiResponse} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import type {useVehicleCatalogue} from '@dms/shared';

import {parseDate, type Nullish} from 'shared/web';

const KILOWATTS_TO_HORSEPOWER = 1.35962;

export type Specification = {
  label: string;
  value: string | number | Nullish;
  color?: string | Nullish;
};

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
export function getVehicleSpecifications({
  specificationsVehicle,
  selectedPlatform,
  catalogue,
  isCatalogueLoading,
  formatDate,
  formatNumber = (value) => (isNil(value) ? '' : value.toString()),
  countryName,
}: {
  specificationsVehicle: GetVehicleApiResponse;
  selectedPlatform?: AdvertisementPreviewPlatform;
  catalogue: ReturnType<typeof useVehicleCatalogue>[0];
  isCatalogueLoading: boolean;
  formatDate: ReturnType<typeof useDateTimeFormatter>;
  formatNumber?: ReturnType<typeof useFormatNumber>;
  countryName?: (code: string) => string | Nullish;
}): {
  left: Specification[];
  right: Specification[];
  groups: Array<{id: string; title: string; rows: Specification[]}>;
} {
  const label = (name: string) => i18n.t(`entity.vehicle.labels.${name}`);
  const catalogueLabel = (value: string | Nullish, getLabel: (key: string) => string | Nullish) =>
    typeof value === 'string' && isNotNilOrEmpty(value) && !isCatalogueLoading
      ? getLabel(value)
      : undefined;
  const exteriorColor = specificationsVehicle.exteriorColor?.code;
  const firstRegistrationDate = isNotNilOrEmpty(specificationsVehicle.firstRegistrationOn)
    ? parseDate(specificationsVehicle.firstRegistrationOn)
    : null;
  const isElectricConsumption =
    specificationsVehicle.fuelType === 'FUELTYPE_ELECTRIC' ||
    specificationsVehicle.hybridType === 'HYBRIDTYPE_FULL';
  const consumptionFields = isElectricConsumption
    ? [
        specificationsVehicle.fuelConsumptionWltpCombinedKwh,
        specificationsVehicle.fuelConsumptionWltpCityCenterKwh,
        specificationsVehicle.fuelConsumptionWltpOutskirtsKwh,
        specificationsVehicle.fuelConsumptionWltpCountryRoadsKwh,
        specificationsVehicle.fuelConsumptionWltpHighwayKwh,
        specificationsVehicle.fuelConsumptionWltpWeightedCombinedKwh,
      ]
    : [
        specificationsVehicle.fuelConsumptionWltpCombined,
        specificationsVehicle.fuelConsumptionWltpCityCenter,
        specificationsVehicle.fuelConsumptionWltpOutskirts,
        specificationsVehicle.fuelConsumptionWltpCountryRoads,
        specificationsVehicle.fuelConsumptionWltpHighway,
        specificationsVehicle.fuelConsumptionWltpWeighted,
      ];
  const hasWltp = consumptionFields.some(isNotNilOrEmpty);
  const consumptionUnit = i18n.t(
    isElectricConsumption ? 'general.metric.kwh100km' : 'general.metric.l100km'
  );
  const consumptionValue = (value: string | Nullish) =>
    isNotNilOrEmpty(value) ? `${value} ${consumptionUnit}` : undefined;
  const vinRow: Specification = {
    label: label('vin'),
    value: match(selectedPlatform)
      .with(undefined, () => specificationsVehicle.vin)
      .otherwise((platform) => (platform.vinHidden ? undefined : platform.vin)),
  };
  const left: Specification[] = [
    vinRow,
    {
      label: label('firstRegistration'),
      value: !isNil(firstRegistrationDate)
        ? formatDate('dateShort', firstRegistrationDate)
        : undefined,
    },
    {
      label: label('body'),
      value: catalogueLabel(specificationsVehicle.bodyStyle, catalogue.getBodyStyle),
    },
    {
      label: label('bodyColor'),
      value:
        catalogueLabel(exteriorColor, catalogue.getBodyColor) ??
        specificationsVehicle.exteriorColor?.name,
      color:
        typeof exteriorColor === 'string' && isNotNilOrEmpty(exteriorColor)
          ? catalogue.getBodyColorHEX(exteriorColor)
          : undefined,
    },
    {
      label: label('numberOfDoorsFull'),
      value: catalogueLabel(specificationsVehicle.doorCountCategory, catalogue.getDoorCount),
    },
    {label: label('numberOfSeatsFull'), value: specificationsVehicle.seatCount},
  ];
  const right: Specification[] = [
    {
      label: label('fuel'),
      value: catalogueLabel(specificationsVehicle.fuelType, catalogue.getFuelType),
    },
    {
      label: label('maximalPower'),
      value: isNil(specificationsVehicle.maximalPower)
        ? undefined
        : `${specificationsVehicle.maximalPower} ${i18n.t('general.metric.kW')}`,
    },
    {
      label: label('engineCapacity'),
      value: isNil(specificationsVehicle.engineVolume)
        ? undefined
        : `${specificationsVehicle.engineVolume} ${i18n.t('general.metric.ccm')}`,
    },
    {
      label: label('transmission'),
      value: catalogueLabel(specificationsVehicle.transmission, catalogue.getTransmission),
    },
    {
      label: label('emissionClass'),
      value: catalogueLabel(specificationsVehicle.emissionClass, catalogue.getEmissionClass),
    },
    ...match({hasWltp, isElectricConsumption})
      .with({hasWltp: true, isElectricConsumption: true}, () => [
        {
          label: label('fuelConsumptionWltpCombined'),
          value: consumptionValue(specificationsVehicle.fuelConsumptionWltpCombinedKwh),
        },
        {
          label: label('fuelConsumptionWltpCityCenter'),
          value: consumptionValue(specificationsVehicle.fuelConsumptionWltpCityCenterKwh),
        },
        {
          label: label('fuelConsumptionWltpOutskirts'),
          value: consumptionValue(specificationsVehicle.fuelConsumptionWltpOutskirtsKwh),
        },
        {
          label: label('fuelConsumptionWltpCountryRoadsKwh'),
          value: consumptionValue(specificationsVehicle.fuelConsumptionWltpCountryRoadsKwh),
        },
        {
          label: label('fuelConsumptionWltpHighwayKwh'),
          value: consumptionValue(specificationsVehicle.fuelConsumptionWltpHighwayKwh),
        },
        {
          label: label('fuelConsumptionWltpWeightedCombinedKwh'),
          value: consumptionValue(specificationsVehicle.fuelConsumptionWltpWeightedCombinedKwh),
        },
      ])
      .with({hasWltp: true, isElectricConsumption: false}, () => [
        {
          label: label('fuelConsumptionWltpCombined'),
          value: consumptionValue(consumptionFields[0]),
        },
        {
          label: label('fuelConsumptionWltpCityCenter'),
          value: consumptionValue(consumptionFields[1]),
        },
        {
          label: label('fuelConsumptionWltpOutskirts'),
          value: consumptionValue(consumptionFields[2]),
        },
        {
          label: label('fuelConsumptionWltpCountryRoads'),
          value: consumptionValue(consumptionFields[3]),
        },
        {
          label: label('fuelConsumptionWltpHighway'),
          value: consumptionValue(consumptionFields[4]),
        },
        {
          label: label('fuelConsumptionWltpWeighted'),
          value: consumptionValue(specificationsVehicle.fuelConsumptionWltpWeighted),
        },
      ])
      .with({hasWltp: false, isElectricConsumption: true}, () => [
        {
          label: label('powerConsumptionCombined'),
          value: consumptionValue(specificationsVehicle.powerConsumptionCombined),
        },
      ])
      .with({hasWltp: false, isElectricConsumption: false}, () => [
        {
          label: label('fuelConsumptionCombined'),
          value: consumptionValue(specificationsVehicle.fuelConsumptionCombined),
        },
        {
          label: label('fuelConsumptionUrban'),
          value: consumptionValue(specificationsVehicle.fuelConsumptionUrban),
        },
        {
          label: label('fuelConsumptionExtraUrban'),
          value: consumptionValue(specificationsVehicle.fuelConsumptionExtraUrban),
        },
      ])
      .exhaustive(),
    {
      label: label('emissionsCo2WO'),
      value: isNotNilOrEmpty(specificationsVehicle.carbonDioxideEmission)
        ? `${specificationsVehicle.carbonDioxideEmission} ${i18n.t('general.metric.gkm')}`
        : undefined,
    },
  ];
  const visibleLeft = left.filter(({value}) => isNotNilOrEmpty(value));
  const visibleRight = right.filter(({value}) => isNotNilOrEmpty(value));
  const metric = (value: number | Nullish, unit: string) =>
    isNil(value) ? undefined : `${formatNumber(value)} ${i18n.t(`general.metric.${unit}`)}`;
  const date = (value: string | Nullish) => {
    const parsed = isNotNilOrEmpty(value) ? parseDate(value) : null;
    return isNil(parsed) ? undefined : formatDate('dateShort', parsed);
  };
  const manufacturedOn = isNotNilOrEmpty(specificationsVehicle.manufacturedOn)
    ? parseDate(specificationsVehicle.manufacturedOn)
    : null;
  const dimensions = specificationsVehicle.dimensions;
  const dimensionsValue =
    isNotNil(dimensions) &&
    isNotNilOrEmpty(dimensions.length) &&
    isNotNilOrEmpty(dimensions.width) &&
    isNotNilOrEmpty(dimensions.height)
      ? `${dimensions.length} × ${dimensions.width} × ${dimensions.height} ${i18n.t('general.metric.mm')}`
      : undefined;
  const lastService = [
    date(specificationsVehicle.lastServiceDate),
    metric(specificationsVehicle.lastServiceMileage, 'km'),
  ]
    .filter(isNotNilOrEmpty)
    .join(' / ');
  const originCountry = specificationsVehicle.originCountry;
  const originCountryName =
    typeof originCountry === 'string' && isNotNilOrEmpty(originCountry)
      ? countryName?.(originCountry)
      : undefined;
  const countryOfOrigin = isNotNilOrEmpty(specificationsVehicle.originCountryName)
    ? specificationsVehicle.originCountryName
    : originCountryName;
  const groups = [
    {
      id: 'history',
      title: i18n.t('page.advertisement.preview.conditionAndHistory'),
      rows: [
        {
          label: label('condition'),
          value: catalogueLabel(
            specificationsVehicle.condition,
            (key) =>
              catalogue.vehicleConditionOptions?.find((option) => option.value === key)?.label
          ),
        },
        vinRow,
        {
          label: label('mileage'),
          value: metric(
            isNotNil(selectedPlatform) ? selectedPlatform.mileage : specificationsVehicle.mileage,
            'km'
          ),
        },
        left[1],
        {
          label: label('manufactureYear'),
          value: isNil(manufacturedOn) ? undefined : getYear(manufacturedOn).toString(),
        },
        {label: label('countryOfOrigin'), value: countryOfOrigin},
        {label: label('previousOwners'), value: specificationsVehicle.ownerCount},
      ],
    },
    {
      id: 'vehicle',
      title: i18n.t('entity.vehicle.labels.vehicleSpecification'),
      rows: [
        left[2],
        {label: label('trim'), value: specificationsVehicle.trim},
        left[3],
        {
          label: label('manufacturerColorName'),
          value:
            specificationsVehicle.manufacturerColorName ??
            specificationsVehicle.exteriorColorFactoryName,
        },
        {
          label: label('interiorMaterial'),
          value: catalogueLabel(
            specificationsVehicle.interiorMaterial,
            catalogue.getInteriorMaterial
          ),
        },
        {
          label: label('interiorColor'),
          value:
            catalogueLabel(
              specificationsVehicle.interiorColor?.code,
              (key) => catalogue.interiorColorOptions?.find((option) => option.value === key)?.label
            ) ??
            specificationsVehicle.interiorColor?.name ??
            specificationsVehicle.interiorColorName,
        },
        left[4],
        left[5],
      ],
    },
    {
      id: 'engine',
      title: i18n.t('entity.newVehicle.labels.engineDrive'),
      rows: [
        right[0],
        {
          label: label('maximalPower'),
          value: isNil(specificationsVehicle.maximalPower)
            ? undefined
            : `${metric(specificationsVehicle.maximalPower, 'kW')} (${formatNumber(Math.round(specificationsVehicle.maximalPower * KILOWATTS_TO_HORSEPOWER))} ${i18n.t('entity.newVehicle.labels.ps')})`,
        },
        right[2],
        {label: label('numberOfCylinders2'), value: specificationsVehicle.cylinderCount},
        right[3],
        {label: label('numberOfGears2'), value: specificationsVehicle.gearCount},
        {
          label: label('drive'),
          value: catalogueLabel(specificationsVehicle.drive, catalogue.getDrive),
        },
      ],
    },
    {
      id: 'service',
      title: i18n.t('page.advertisement.preview.serviceAndDocuments'),
      rows: [
        {
          label: label('inspectionValidUntil'),
          value: date(specificationsVehicle.technicalInspectionValidUntil),
        },
        {
          label: label('serviceBook'),
          value: catalogueLabel(
            specificationsVehicle.serviceBookType,
            (key) => catalogue.serviceBookTypeOptions?.find((option) => option.value === key)?.label
          ),
        },
        {
          label: label('servicedInAuthorizedNetwork'),
          value: isNil(specificationsVehicle.servicedInAuthorizedNetwork)
            ? undefined
            : i18n.t(
                specificationsVehicle.servicedInAuthorizedNetwork
                  ? 'general.labels.yes'
                  : 'general.labels.no'
              ),
        },
        {label: label('lastService'), value: lastService},
      ],
    },
    {
      id: 'dimensions',
      title: i18n.t('page.advertisement.preview.dimensionsAndWeight'),
      rows: [
        {label: label('dimensions'), value: dimensionsValue},
        {label: label('wheelBase'), value: metric(specificationsVehicle.wheelBase, 'mm')},
        {
          label: label('operatingWeight'),
          value: metric(specificationsVehicle.operatingWeight, 'kg'),
        },
        {
          label: i18n.t('entity.vehicle.labels.maxLoadCapacity'),
          value: metric(
            specificationsVehicle.maximalLoadCapacity ?? specificationsVehicle.loadingCapacity,
            'kg'
          ),
        },
        {label: label('trunkSpace'), value: metric(specificationsVehicle.trunkSpace, 'l')},
      ],
    },
    {
      id: 'consumption',
      title: i18n.t('entity.vehicle.labels.consumptionAndEmissionSectionTitle'),
      rows: [
        {
          label: i18n.t('page.advertisement.preview.measurementStandard'),
          value: hasWltp ? 'WLTP' : undefined,
        },
        ...right.slice(4),
      ],
    },
  ]
    .map((group) => ({...group, rows: group.rows.filter((row) => isNotNilOrEmpty(row.value))}))
    .filter((group) => group.rows.length > 0);

  return {left: visibleLeft, right: visibleRight, groups};
}
